import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle, ArrowRight, TrendingUp, Shield } from 'lucide-react';

export const Hero = () => {
  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden pt-20">
      {/* Abstract gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-secondary/30 to-background">
        {/* Decorative gradient orbs with Google colors */}
        <div className="absolute top-20 right-1/4 w-[600px] h-[600px] bg-[#4285F4]/8 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-[#34A853]/6 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] bg-[#EA4335]/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        
        {/* Subtle grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="max-w-2xl">
            {/* Headline */}
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 animate-fade-in-up">
              Innovate{' '}
              <span className="text-[#4285F4]">Elevate</span>{' '}
              Achieve
            </h1>

            {/* Subheadline */}
            <p className="text-lg text-muted-foreground leading-relaxed mb-8 animate-fade-in-up animation-delay-100">
              Drive measurable growth with Google Ads, Google Tag Manager & Google Analytics. 
              Expert optimization from a former <span className="font-semibold text-foreground">Google specialist</span>.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-10 animate-fade-in-up animation-delay-200">
              <Link to="/booking" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                <Button className="w-full sm:w-auto bg-[#EA4335] hover:bg-[#EA4335]/90 text-white rounded-full px-8 py-6 text-base font-semibold group">
                  Book Free Audit
                  <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <a href="#services">
                <Button className="w-full sm:w-auto bg-[#FBBC05] hover:bg-[#FBBC05]/90 text-black rounded-full px-8 py-6 text-base font-semibold">
                  Learn More
                </Button>
              </a>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center gap-6 animate-fade-in-up animation-delay-300">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle className="h-5 w-5 text-[#34A853]" />
                <span>Google Insider</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle className="h-5 w-5 text-[#4285F4]" />
                <span>Data-Driven</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle className="h-5 w-5 text-[#FBBC05]" />
                <span>Full Implementation</span>
              </div>
            </div>
          </div>

          {/* Right Content - Abstract Visual */}
          <div className="relative animate-fade-in-up animation-delay-300 hidden lg:block">
            {/* Main visual container */}
            <div className="relative">
              {/* Central card with Google services logos */}
              <div className="bg-card/80 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-border/50">
                <div className="grid grid-cols-3 gap-6">
                  {/* Google Ads */}
                  <div className="flex flex-col items-center gap-3 p-4 rounded-2xl bg-secondary/50 hover:bg-secondary transition-colors">
                    <img 
                      src="https://upload.wikimedia.org/wikipedia/commons/c/c7/Google_Ads_logo.svg" 
                      alt="Google Ads" 
                      className="w-12 h-12"
                    />
                    <span className="text-xs font-medium text-foreground">Google Ads</span>
                  </div>
                  
                  {/* Google Tag Manager */}
                  <div className="flex flex-col items-center gap-3 p-4 rounded-2xl bg-secondary/50 hover:bg-secondary transition-colors">
                    <img 
                      src="https://www.gstatic.com/analytics-suite/header/suite/v2/ic_tag_manager.svg" 
                      alt="Google Tag Manager" 
                      className="w-12 h-12"
                    />
                    <span className="text-xs font-medium text-foreground">Tag Manager</span>
                  </div>
                  
                  {/* Google Analytics */}
                  <div className="flex flex-col items-center gap-3 p-4 rounded-2xl bg-secondary/50 hover:bg-secondary transition-colors">
                    <img 
                      src="https://www.gstatic.com/analytics-suite/header/suite/v2/ic_analytics.svg" 
                      alt="Google Analytics" 
                      className="w-12 h-12"
                    />
                    <span className="text-xs font-medium text-foreground">Analytics</span>
                  </div>
                </div>
                
                {/* Stats row */}
                <div className="mt-6 pt-6 border-t border-border/50 grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#4285F4]">+240%</p>
                    <p className="text-xs text-muted-foreground">Avg. ROAS Increase</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#34A853]">100%</p>
                    <p className="text-xs text-muted-foreground">Data Accuracy</p>
                  </div>
                </div>
              </div>

              {/* Floating Stats Card - positioned lower */}
              <div className="absolute -bottom-12 -left-6 bg-card rounded-xl p-4 shadow-xl border border-border animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#34A853]/10 flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-[#34A853]" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-foreground">Google Expert</p>
                    <p className="text-xs text-muted-foreground">Former Insider</p>
                  </div>
                </div>
              </div>

              {/* Floating Badge */}
              <div className="absolute -top-4 -right-4 bg-card rounded-xl p-3 shadow-xl border border-border animate-float animation-delay-500">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden">
                    <img 
                      src="https://www.google.com/favicon.ico" 
                      alt="Google certified partner" 
                      width="24"
                      height="24"
                      loading="lazy"
                      className="w-6 h-6"
                    />
                  </div>
                  <span className="text-sm font-medium text-foreground">Certified</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
